/* eslint max-lines: ["error", { "max": 585 }] */
import { yupResolver } from '@hookform/resolvers/yup';
import {
  ClassDefinitionDto,
  DependentTypeEnum,
  EmployeeDto,
  PossibleFipsCode,
} from '@zorro/clients';
import {
  convertFromMaybeYesNo,
  getFieldRequirementsFromClassDefinitions,
  logger,
} from '@zorro/common-utils';
import { getNowAsDate, tryFormatDateISO } from '@zorro/shared/formatters';
import {
  useForm,
  useImpersonation,
  useLoadingOverlay,
  useMonolithMutation,
  useMonolithQuery,
  useRoles,
} from '@zorro/web/ui-utils';
import { useEffect, useMemo, useState } from 'react';

import {
  InsuredDependentsFormFields,
  getDependentSchema,
  getDependentsFormSchema,
} from './DependentsFormInputs';
import {
  InsuredEmployeeFormFields,
  getEmployeeFormSchema,
} from './EmployeeFormInputs';
import {
  InsuredFormValidity,
  SpouseStatus,
  UseInsuredFormReturnType,
  convertDependentSubtypeDtoToForm,
  getDefaultInsuredFormFields,
  getInvalidTabs,
  getStoredHouseholdFromEmployee,
  mapDependentDtoToBaseInsuredFormFields,
  mapFormFieldsToDependentDto,
  mapInsuredDtoToBaseInsuredFormFields,
  validateAllAddressesAndGetFailedTabs,
} from './InsuredFormUtils';
import {
  InsuredSpouseFormFields,
  getSpouseInsuredSchema,
} from './SpouseFormInputs';
import { useFollowEmployeeAddress } from './useFollowEmployeeAddress';

const withAnticipatedCareLevel = false;

type Props = {
  employeeId: string;
  isFinalizationMode: boolean;
};

const getRequiredClassCategories = (classDefinitions: ClassDefinitionDto[]) => {
  const now = getNowAsDate();
  const currentYear = now.getFullYear();

  const classDefinitionsForTargetYear = classDefinitions.filter(
    (classDefinition) =>
      classDefinition.year === currentYear ||
      classDefinition.year === currentYear + 1
  );
  return getFieldRequirementsFromClassDefinitions(
    classDefinitionsForTargetYear.flatMap(
      (classDefinition) => classDefinition.items
    )
  );
};

const getChangedEmployeeFields = (
  formValues: InsuredEmployeeFormFields,
  originalEmployee: EmployeeDto | undefined,
  isMailingAddressSameAsResidentialAddress?: boolean
  // eslint-disable-next-line sonarjs/cognitive-complexity
): { partialDto: Partial<EmployeeDto> } => {
  if (!originalEmployee) {
    throw new Error('Cannot get changed fields: originalEmployee is required');
  }

  const partialDto: Partial<EmployeeDto> = {};

  if (formValues.firstName !== originalEmployee.firstName) {
    partialDto.firstName = formValues.firstName;
  }
  if (formValues.lastName !== originalEmployee.lastName) {
    partialDto.lastName = formValues.lastName;
  }
  if (formValues.gender !== originalEmployee.gender) {
    partialDto.gender = formValues.gender;
  }
  if (formValues.residentialAddress !== originalEmployee.address) {
    partialDto.address = formValues.residentialAddress;
  }
  if (formValues.phone !== originalEmployee.phone) {
    partialDto.phone = formValues.phone;
  }
  if (formValues.ssn !== originalEmployee.ssn) {
    partialDto.ssn = formValues.ssn;
  }
  if (formValues.employmentType !== originalEmployee.employmentType) {
    partialDto.employmentType = formValues.employmentType;
  }
  if (formValues.wageType !== originalEmployee.wageType) {
    partialDto.wageType = formValues.wageType;
  }
  if (formValues.personalEmail !== originalEmployee.personalEmail) {
    partialDto.personalEmail = formValues.personalEmail || null;
  }
  // Only a county the user actively picked is worth sending; an empty field
  // means "server, work it out from the address"
  if (
    formValues.fipsCode &&
    formValues.fipsCode !== originalEmployee.fipsCode
  ) {
    partialDto.fipsCode = formValues.fipsCode;
  }
  if (formValues.citizenshipStatus !== originalEmployee.citizenshipStatus) {
    partialDto.citizenshipStatus = formValues.citizenshipStatus;
  }
  if (formValues.maritalStatus !== originalEmployee.maritalStatus) {
    partialDto.maritalStatus = formValues.maritalStatus;
  }
  if (formValues.customClassCategory !== originalEmployee.customClassCategory) {
    partialDto.customClassCategory = formValues.customClassCategory;
  }

  const formDateOfBirth = tryFormatDateISO(formValues.dateOfBirth);
  if (formDateOfBirth !== originalEmployee.dateOfBirth) {
    partialDto.dateOfBirth = formDateOfBirth;
  }

  const currentMailingAddress = isMailingAddressSameAsResidentialAddress
    ? formValues.residentialAddress
    : formValues.mailingAddress;
  if (currentMailingAddress !== originalEmployee.mailingAddress) {
    partialDto.mailingAddress = currentMailingAddress;
  }

  if (
    convertFromMaybeYesNo(formValues.isSmoker) !== originalEmployee.isSmoker
  ) {
    partialDto.isSmoker = convertFromMaybeYesNo(formValues.isSmoker);
  }

  if (
    convertFromMaybeYesNo(formValues.isPregnant) !== originalEmployee.isPregnant
  ) {
    partialDto.isPregnant = convertFromMaybeYesNo(formValues.isPregnant);
  }

  return { partialDto };
};

export const useGlobalInsuredForm = ({
  employeeId,
}: Props): UseInsuredFormReturnType => {
  'use no memo';
  const { startLoading, stopLoading } = useLoadingOverlay();
  const { isAgent, isZorroOperations, isEmployerAdmin } = useRoles();
  const { isImpersonated } = useImpersonation();
  const [employeePossibleFipsCodes, setEmployeePossibleFipsCodes] = useState<
    PossibleFipsCode[]
  >([]);
  const [spousePossibleFipsCodes, setSpousePossibleFipsCodes] = useState<
    PossibleFipsCode[]
  >([]);
  const [dependentPossibleFipsCodes, setDependentPossibleFipsCodes] = useState<
    Record<number, PossibleFipsCode[]>
  >({});
  const [openedTabs, setOpenedTabs] = useState<string[]>(['EMPLOYEE']);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const isFinalizationMode = !isZorroOperations;
  const { mutate: mutateEmployee } = useMonolithMutation(
    isZorroOperations || isAgent || isEmployerAdmin || isImpersonated
      ? {
          method: 'employeesControllerUpdateByAdmin',
        }
      : {
          method: 'employeesControllerUpdateSelf',
        }
  );

  const { mutate: mutateDependents } = useMonolithMutation({
    method: 'employeesControllerUpsertDependents',
  });

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const { data: { dependents } = { dependents: undefined } } = useMonolithQuery(
    {
      method: 'employeesControllerGetDependents',
      params: [employeeId],
    }
  );

  const { data: { classDefinitions } = { classDefinitions: [] } } =
    useMonolithQuery({
      method: 'allowanceModelControllerGetAllClassDefinitionsForEmployer',
      params: employee?.employerId && [employee.employerId],
    });

  const requiredClassCategories = getRequiredClassCategories(classDefinitions);

  const employeeForm = useForm<InsuredEmployeeFormFields>({
    mode: 'all',
    resolver: yupResolver(
      getEmployeeFormSchema(
        isFinalizationMode,
        withAnticipatedCareLevel,
        requiredClassCategories
      )
    ),
    defaultValues: {
      ...mapInsuredDtoToBaseInsuredFormFields(
        withAnticipatedCareLevel,
        employee
      ),
      email: employee?.email || '',
      phone: employee?.phone || '',
      personalEmail: employee?.personalEmail || '',
      residentialAddress: employee?.address || '',
      isMailingAddressSameAsResidentialAddress:
        employee?.address && employee?.mailingAddress
          ? employee.address === employee.mailingAddress
          : true,
      mailingAddress: employee?.mailingAddress || '',
      employmentType: employee?.employmentType || null,
      wageType: employee?.wageType || null,
      // Left empty on purpose - see useEnrollmentInsuredForm
      fipsCode: '',
      citizenshipStatus: employee?.citizenshipStatus || null,
      maritalStatus: employee?.maritalStatus || null,
      customClassCategory: employee?.customClassCategory || null,
    } satisfies InsuredEmployeeFormFields,
  });

  const spouse = useMemo(
    () =>
      dependents?.find(
        (dependent) =>
          dependent.type === DependentTypeEnum.SPOUSE_OR_DOMESTIC_PARTNER
      ),
    [dependents]
  );

  const [spouseStatus, setSpouseStatus] = useState<SpouseStatus>({
    spouseWasRemoved: false,
    isSpouseActive: false,
  });

  const otherDependents = useMemo(
    () =>
      dependents?.filter(
        (dependent) =>
          dependent.type === DependentTypeEnum.CHILD_OR_OTHER_DEPENDENT
      ),
    [dependents]
  );

  const spouseForm = useForm<InsuredSpouseFormFields>({
    mode: 'all',
    resolver: yupResolver(
      getSpouseInsuredSchema(isFinalizationMode, withAnticipatedCareLevel)
    ),
    defaultValues:
      spouse && !spouseStatus.spouseWasRemoved
        ? {
            ...mapDependentDtoToBaseInsuredFormFields(spouse),
            subtype: convertDependentSubtypeDtoToForm(spouse.subType),
          }
        : getDefaultInsuredFormFields(employee?.address),
  });

  const dependentSchema = getDependentSchema(
    isFinalizationMode,
    withAnticipatedCareLevel
  );

  const dependentsForm = useForm<InsuredDependentsFormFields>({
    mode: 'all',
    defaultValues: {
      dependents:
        otherDependents && otherDependents.length > 0
          ? otherDependents.map((dependent) => ({
              ...mapDependentDtoToBaseInsuredFormFields(dependent),
              subtype: convertDependentSubtypeDtoToForm(dependent.subType),
              insuredId: dependent.id,
              isDisabled: Boolean(dependent.isDisabled),
            }))
          : [],
    },
    resolver: yupResolver(getDependentsFormSchema(dependentSchema)),
  });

  // Read on every render to keep react-hook-form revalidating each sub-form.
  // See InsuredFormValidity for why a later-mounting section is not enough.
  const formValidity: InsuredFormValidity = {
    isEmployeeValid: employeeForm.formState.isValid,
    isSpouseValid: spouseForm.formState.isValid,
    areDependentsValid: dependentsForm.formState.isValid,
  };

  const hasAnyDependents = dependentsForm.watch('dependents').length > 0;
  const employeeAddress = employeeForm.watch('residentialAddress');

  const cancelSpouse = () => {
    if (spouse) {
      spouseForm.reset({
        ...mapDependentDtoToBaseInsuredFormFields(spouse),
        subtype: convertDependentSubtypeDtoToForm(spouse.subType),
      });
      setSpouseStatus({
        isSpouseActive: true,
        spouseWasRemoved: false,
      });
    } else {
      spouseForm.reset();
    }
  };

  useFollowEmployeeAddress({
    employeeAddress,
    spouseForm,
    dependentsForm,
    clearSpousePossibleFipsCodes: () => setSpousePossibleFipsCodes([]),
    clearDependentPossibleFipsCodes: (index) =>
      setDependentPossibleFipsCodes((prev) => ({ ...prev, [index]: [] })),
  });

  useEffect(() => {
    setSpouseStatus((prev) => {
      const isSpouseActive = Boolean(spouse);
      if (prev.isSpouseActive === isSpouseActive) {
        return prev;
      }
      return {
        ...prev,
        isSpouseActive,
      };
    });
  }, [spouse, setSpouseStatus]);

  return {
    employeeForm,
    spouseForm,
    dependentsForm,
    formValidity,
    hasAttemptedSubmit,
    dependentSchema,
    hasAnyDependents,
    employeeZipCode: employee?.zipCode,
    employeeFipsCode: employee?.fipsCode,
    possibleFipsCodes: employeePossibleFipsCodes,
    setPossibleFipsCodes: setEmployeePossibleFipsCodes,
    spousePossibleFipsCodes,
    setSpousePossibleFipsCodes,
    dependentPossibleFipsCodes,
    setDependentPossibleFipsCodes: (index: number, codes: PossibleFipsCode[]) =>
      setDependentPossibleFipsCodes((prev) => ({ ...prev, [index]: codes })),
    spouseStatus,
    setSpouseStatus,
    isDomesticPartnerAllowed: true,
    cancelSpouse,
    openedTabs,
    setOpenedTabs,
    onSubmit: async (): Promise<boolean> => {
      setHasAttemptedSubmit(true);
      startLoading();
      const validationResults = await Promise.all([
        employeeForm.trigger(),
        spouseStatus.isSpouseActive ? spouseForm.trigger() : true,
        hasAnyDependents ? dependentsForm.trigger() : true,
      ]);

      if (validationResults.some((result) => !result)) {
        setOpenedTabs(
          getInvalidTabs(
            employeeForm.formState.isValid,
            spouseStatus.isSpouseActive,
            spouseForm.formState.isValid,
            hasAnyDependents,
            dependentsForm.getValues().dependents.length,
            (index) =>
              Boolean(dependentsForm.formState.errors?.dependents?.[index])
          )
        );
        stopLoading();
        return false;
      }

      const formValues = employeeForm.getValues();
      const { isMailingAddressSameAsResidentialAddress, fipsCode } = formValues;
      const spouseFormValues = spouseForm.getValues();
      const { dependents } = dependentsForm.getValues();

      const failedTabs = await validateAllAddressesAndGetFailedTabs(
        formValues,
        fipsCode,
        (field, error) => employeeForm.setError(field, error),
        (code) => employeeForm.setValue('fipsCode', code),
        setEmployeePossibleFipsCodes,
        spouseFormValues,
        spouseStatus,
        (field, error) => spouseForm.setError(field, error),
        (code) => spouseForm.setValue('fipsCode', code),
        setSpousePossibleFipsCodes,
        dependents,
        (index, field, error) =>
          dependentsForm.setError(`dependents.${index}.${field}`, error),
        (index, code) =>
          dependentsForm.setValue(`dependents.${index}.fipsCode`, code),
        (index, codes) =>
          setDependentPossibleFipsCodes((prev) => ({
            ...prev,
            [index]: codes,
          })),
        stopLoading,
        getStoredHouseholdFromEmployee(employee, spouse, otherDependents),
        (address) => employeeForm.setValue('residentialAddress', address),
        (address) => spouseForm.setValue('residentialAddress', address),
        (index, address) =>
          dependentsForm.setValue(
            `dependents.${index}.residentialAddress`,
            address
          )
      );

      if (failedTabs.length > 0) {
        setOpenedTabs(failedTabs);
        return false;
      }

      // Re-read form values to pick up fipsCodes set during disambiguation
      const resolvedFormValues = employeeForm.getValues();
      const resolvedSpouse = spouseForm.getValues();
      const { dependents: resolvedDependents } = dependentsForm.getValues();

      let result = false;
      try {
        const { partialDto } = getChangedEmployeeFields(
          resolvedFormValues,
          employee,
          isMailingAddressSameAsResidentialAddress
        );

        if (Object.keys(partialDto).length > 0) {
          await mutateEmployee([employeeId, partialDto]);
        }

        const insuredSpouse = spouseStatus.isSpouseActive
          ? [
              mapFormFieldsToDependentDto(
                resolvedSpouse,
                DependentTypeEnum.SPOUSE_OR_DOMESTIC_PARTNER,
                spouseStatus.spouseWasRemoved ? undefined : spouse?.id
              ),
            ]
          : [];
        const insuredDependents = hasAnyDependents
          ? resolvedDependents.map((dependent) =>
              mapFormFieldsToDependentDto(
                dependent,
                DependentTypeEnum.CHILD_OR_OTHER_DEPENDENT,
                dependent.insuredId
              )
            )
          : [];

        const insuredDtos = [...insuredSpouse, ...insuredDependents];

        await mutateDependents([
          employeeId,
          {
            dependents: insuredDtos,
          },
        ]);

        setSpouseStatus((prev) => ({
          ...prev,
          spouseWasRemoved: false,
        }));

        result = true;
      } catch (error) {
        logger.error(error);
      } finally {
        stopLoading();
      }
      return result;
    },
  };
};
